import React, { useEffect, useState, useMemo } from "react"
import { connect } from "react-redux"
import { withTranslation } from "react-i18next"
import MetaTags from "react-meta-tags"
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Table,
  Tag,
  Space,
  Typography,
  Statistic,
  Modal,
  Form,
  message,
  Tooltip,
  Badge,
  Collapse,
  Divider,
  Switch,
  Empty,
  Spin,
  Alert,
  Grid,
  Select,
} from "antd"
import {
  ToolOutlined,
  FileTextOutlined,
  UserOutlined,
  HomeOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  NumberOutlined,
  CalendarOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  SendOutlined,
  InboxOutlined,
} from "@ant-design/icons"

// Store
import {
  getAllProjectPackages,
  updateProjectPackageSerial,
  resetProjectPackagesFlag,
} from "store/actions"

// Normalize serial to what backend accepts (remove Persian/Arabic chars, non-ASCII, and map Persian digits)
const normalizeSerialInput = value => {
  if (!value) return ""

  const persianDigitMap = {
    "۰": "0",
    "۱": "1",
    "۲": "2",
    "۳": "3",
    "۴": "4",
    "۵": "5",
    "۶": "6",
    "۷": "7",
    "۸": "8",
    "۹": "9",
  }

  const withEnglishDigits = value.replace(/[۰-۹]/g, d => persianDigitMap[d] || "")
  const withoutPersianArabic = withEnglishDigits.replace(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g, "")
  const onlyAllowed = withoutPersianArabic.replace(/[^a-zA-Z0-9]/g, "")

  return onlyAllowed
}

const { Title, Text, Paragraph } = Typography
const { Panel } = Collapse
const { TextArea } = Input
const { useBreakpoint } = Grid
const { Option } = Select

const ProjectPackageList = ({
  allProjectPackages,
  loading,
  updateSerialLoading,
  updateSerialSuccess,
  updateSerialError,
  onGetAllProjectPackages,
  onUpdateProjectPackageSerial,
  onResetFlag,
  // New props for embedded mode
  gasProjectId = null,
  disableEdit = false,
  embedded = false,
}) => {
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const [submitFilter, setSubmitFilter] = useState(null) // null = all, true = submitted, false = pending
  const [searchText, setSearchText] = useState("")
  const [editingPackage, setEditingPackage] = useState(null)
  const [form] = Form.useForm()
  const rawSerial = Form.useWatch("serialNumber", form)
  const normalizedSerialPreview = useMemo(
    () => normalizeSerialInput(rawSerial || ""),
    [rawSerial]
  )
  const serialDiffersFromInput = useMemo(
    () => !!rawSerial && rawSerial !== normalizedSerialPreview,
    [rawSerial, normalizedSerialPreview]
  )

  // Load data on mount
  useEffect(() => {
    onGetAllProjectPackages(gasProjectId)
  }, [onGetAllProjectPackages, gasProjectId])

  // Handle success/error messages
  useEffect(() => {
    if (updateSerialSuccess) {
      message.success("سریال با موفقیت ثبت شد")
      setEditingPackage(null)
      form.resetFields()
      onResetFlag()
    }
    if (updateSerialError) {
      message.error(updateSerialError || "خطا در ثبت سریال")
      onResetFlag()
    }
  }, [updateSerialSuccess, updateSerialError, onResetFlag, form])

  // Group packages by FileNumber
  const groupedPackages = useMemo(() => {
    if (!allProjectPackages || allProjectPackages.length === 0) return []

    // Filter by submit status
    let filtered = allProjectPackages
    if (submitFilter !== null) {
      filtered = allProjectPackages.filter(pkg => pkg.submit === submitFilter)
    }

    // Filter by search text
    if (searchText) {
      const search = searchText.toLowerCase()
      filtered = filtered.filter(
        pkg =>
          pkg.fileNumber?.toString().includes(search) ||
          pkg.landlordName?.toLowerCase().includes(search) ||
          pkg.postalCode?.toLowerCase().includes(search) ||
          pkg.address?.toLowerCase().includes(search) ||
          pkg.serialNumber?.toLowerCase().includes(search)
      )
    }

    // Group by FileNumber
    const grouped = filtered.reduce((acc, pkg) => {
      const key = pkg.fileNumber
      if (!acc[key]) {
        acc[key] = {
          fileNumber: pkg.fileNumber,
          landlordName: pkg.landlordName,
          postalCode: pkg.postalCode,
          address: pkg.address,
          engineerName: pkg.engineerName,
          executorName: pkg.executorName,
          executorPhoneNumber: pkg.executorPhoneNumber,
          packages: [],
        }
      }
      acc[key].packages.push(pkg)
      return acc
    }, {})

    return Object.values(grouped)
  }, [allProjectPackages, submitFilter, searchText])

  // Statistics
  const stats = useMemo(() => {
    if (!allProjectPackages) return { total: 0, submitted: 0, pending: 0 }
    return {
      total: allProjectPackages.length,
      submitted: allProjectPackages.filter(p => p.submit).length,
      pending: allProjectPackages.filter(p => !p.submit).length,
    }
  }, [allProjectPackages])

  // Handle edit modal
  const handleEdit = pkg => {
    setEditingPackage(pkg)
    form.setFieldsValue({
      serialNumber: pkg.serialNumber || "",
      description: pkg.description || "",
    })
  }

  // Handle submit serial
  const handleSubmitSerial = async () => {
    try {
      const values = await form.validateFields()
      const normalizedSerial = normalizeSerialInput(values.serialNumber)
      onUpdateProjectPackageSerial({
        projectPackageId: editingPackage.id,
        serialNumber: normalizedSerial,
        description: values.description,
        gasProjectId: gasProjectId, // Pass gasProjectId for reload
      })
    } catch (error) {
      console.error("Validation failed:", error)
    }
  }

  // Render package card for mobile
  const renderPackageCard = pkg => (
    <Card
      key={pkg.id}
      size="small"
      style={{
        marginBottom: 8,
        borderRadius: 8,
        border: pkg.submit ? "1px solid #b7eb8f" : "1px solid #ffd591",
        background: pkg.submit ? "#f6ffed" : "#fffbe6",
      }}
    >
      <Row gutter={[8, 8]} align="middle">
        <Col xs={24} sm={12}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Space>
              <ToolOutlined style={{ color: "#1890ff" }} />
              <Text strong>نصاب:</Text>
              <Text>{pkg.packageInstallerName || "—"}</Text>
            </Space>
            {pkg.packageInstallerCompanyName && (
              <Space>
                <HomeOutlined style={{ color: "#52c41a" }} />
                <Text type="secondary">{pkg.packageInstallerCompanyName}</Text>
              </Space>
            )}
            {pkg.packageInstallerMobileNumber && (
              <Space>
                <PhoneOutlined style={{ color: "#722ed1" }} />
                <Text type="secondary">{pkg.packageInstallerMobileNumber}</Text>
              </Space>
            )}
          </Space>
        </Col>
        <Col xs={24} sm={12}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Space>
              <NumberOutlined style={{ color: "#fa8c16" }} />
              <Text strong>سریال:</Text>
              {pkg.serialNumber ? (
                <Tag color="cyan" style={{ fontFamily: "tahoma" }}>
                  {pkg.serialNumber}
                </Tag>
              ) : (
                <Tag color="default">ثبت نشده</Tag>
              )}
            </Space>
            <Space>
              {pkg.submit ? (
                <Tag color="success" icon={<CheckCircleOutlined />}>
                  ارسال شده - {pkg.solarSendDate}
                </Tag>
              ) : (
                <Tag color="warning" icon={<ClockCircleOutlined />}>
                  در انتظار
                </Tag>
              )}
            </Space>
          </Space>
        </Col>
        <Col xs={24}>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Tooltip title={disableEdit ? "امکان ویرایش وجود ندارد" : "ثبت سریال"}>
              <Button
                type="primary"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(pkg)}
                disabled={pkg.submit || disableEdit}
              >
                ثبت سریال
              </Button>
            </Tooltip>
          </Space>
        </Col>
      </Row>
    </Card>
  )

  // Table columns for packages
  const packageColumns = [
    {
      title: "نصاب",
      key: "installer",
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Space>
            <UserOutlined style={{ color: "#1890ff" }} />
            <Text strong>{record.packageInstallerName || "—"}</Text>
          </Space>
          {record.packageInstallerCompanyName && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.packageInstallerCompanyName}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "تلفن",
      key: "phone",
      render: (_, record) => (
        <Text style={{ fontFamily: "tahoma" }}>
          {record.packageInstallerMobileNumber || "—"}
        </Text>
      ),
    },
    {
      title: "سریال",
      key: "serialNumber",
      render: (_, record) =>
        record.serialNumber ? (
          <Tag color="cyan" style={{ fontFamily: "tahoma" }}>
            {record.serialNumber}
          </Tag>
        ) : (
          <Tag color="default">ثبت نشده</Tag>
        ),
    },
    {
      title: "وضعیت",
      key: "status",
      render: (_, record) =>
        record.submit ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            ارسال شده
          </Tag>
        ) : (
          <Tag color="warning" icon={<ClockCircleOutlined />}>
            در انتظار
          </Tag>
        ),
    },
    {
      title: "تاریخ ارسال",
      key: "sendDate",
      render: (_, record) => (
        <Text type="secondary">{record.solarSendDate || "—"}</Text>
      ),
    },
    {
      title: "عملیات",
      key: "actions",
      render: (_, record) => (
        <Tooltip title={disableEdit ? "امکان ویرایش وجود ندارد" : "ثبت سریال"}>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={record.submit || disableEdit}
          >
            ثبت سریال
          </Button>
        </Tooltip>
      ),
    },
  ]

  // Main content (shared between standalone and embedded modes)
  const renderContent = () => (
    <>
      {/* Header with Statistics */}
      <Card
        style={{
          marginBottom: isMobile ? 12 : 24,
          borderRadius: 12,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
        bodyStyle={{ padding: isMobile ? 12 : 24 }}
      >
          {isMobile ? (
            // Mobile Header Layout
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Space align="center">
                <ToolOutlined
                  style={{ fontSize: 28, color: "rgba(255,255,255,0.9)" }}
                />
                <div>
                  <Title level={4} style={{ color: "#fff", margin: 0 }}>
                    پکیج‌های من
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>
                    مدیریت و ثبت سریال پکیج‌ها
                  </Text>
                </div>
              </Space>
              <Row gutter={[8, 8]}>
                <Col span={8}>
                  <Card
                    size="small"
                    style={{
                      borderRadius: 8,
                      textAlign: "center",
                      background: "rgba(255,255,255,0.15)",
                      border: "none",
                    }}
                    bodyStyle={{ padding: 8 }}
                  >
                    <Text
                      strong
                      style={{ fontSize: 20, color: "#fff", display: "block" }}
                    >
                      {stats.total}
                    </Text>
                    <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 10 }}>
                      کل
                    </Text>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card
                    size="small"
                    style={{
                      borderRadius: 8,
                      textAlign: "center",
                      background: "rgba(255,255,255,0.15)",
                      border: "none",
                    }}
                    bodyStyle={{ padding: 8 }}
                  >
                    <Text
                      strong
                      style={{ fontSize: 20, color: "#52c41a", display: "block" }}
                    >
                      {stats.submitted}
                    </Text>
                    <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 10 }}>
                      ارسال شده
                    </Text>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card
                    size="small"
                    style={{
                      borderRadius: 8,
                      textAlign: "center",
                      background: "rgba(255,255,255,0.15)",
                      border: "none",
                    }}
                    bodyStyle={{ padding: 8 }}
                  >
                    <Text
                      strong
                      style={{ fontSize: 20, color: "#faad14", display: "block" }}
                    >
                      {stats.pending}
                    </Text>
                    <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 10 }}>
                      در انتظار
                    </Text>
                  </Card>
                </Col>
              </Row>
            </Space>
          ) : (
            // Desktop Header Layout
            <Row gutter={[24, 16]} align="middle">
              <Col xs={24} md={8}>
                <Space align="center">
                  <ToolOutlined
                    style={{ fontSize: 40, color: "rgba(255,255,255,0.9)" }}
                  />
                  <div>
                    <Title level={3} style={{ color: "#fff", margin: 0 }}>
                      پکیج‌های من
                    </Title>
                    <Text style={{ color: "rgba(255,255,255,0.8)" }}>
                      مدیریت و ثبت سریال پکیج‌ها
                    </Text>
                  </div>
                </Space>
              </Col>
              <Col xs={24} md={16}>
                <Row gutter={16}>
                  <Col xs={8}>
                    <Card
                      size="small"
                      style={{
                        borderRadius: 8,
                        textAlign: "center",
                        background: "rgba(255,255,255,0.15)",
                        border: "none",
                      }}
                    >
                      <Statistic
                        title={
                          <Text style={{ color: "rgba(255,255,255,0.8)" }}>
                            کل پکیج‌ها
                          </Text>
                        }
                        value={stats.total}
                        valueStyle={{ color: "#fff" }}
                        prefix={<InboxOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={8}>
                    <Card
                      size="small"
                      style={{
                        borderRadius: 8,
                        textAlign: "center",
                        background: "rgba(255,255,255,0.15)",
                        border: "none",
                      }}
                    >
                      <Statistic
                        title={
                          <Text style={{ color: "rgba(255,255,255,0.8)" }}>
                            ارسال شده
                          </Text>
                        }
                        value={stats.submitted}
                        valueStyle={{ color: "#52c41a" }}
                        prefix={<CheckCircleOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={8}>
                    <Card
                      size="small"
                      style={{
                        borderRadius: 8,
                        textAlign: "center",
                        background: "rgba(255,255,255,0.15)",
                        border: "none",
                      }}
                    >
                      <Statistic
                        title={
                          <Text style={{ color: "rgba(255,255,255,0.8)" }}>
                            در انتظار
                          </Text>
                        }
                        value={stats.pending}
                        valueStyle={{ color: "#faad14" }}
                        prefix={<ClockCircleOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
        </Card>

        {/* Filters */}
        <Card
          style={{ marginBottom: isMobile ? 12 : 16, borderRadius: 12 }}
          bodyStyle={{ padding: isMobile ? 12 : 24 }}
        >
          {isMobile ? (
            // Mobile Filters Layout
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              <Input
                placeholder="جستجو..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                allowClear
                size="large"
                style={{ borderRadius: 8 }}
              />
              <Row gutter={[8, 8]} align="middle">
                <Col span={16}>
                  <Select
                    value={submitFilter}
                    onChange={value => setSubmitFilter(value)}
                    style={{ width: "100%" }}
                    size="large"
                    placeholder="فیلتر وضعیت"
                  >
                    <Option value={null}>همه</Option>
                    <Option value={true}>
                      <CheckCircleOutlined style={{ color: "#52c41a" }} /> ارسال شده
                    </Option>
                    <Option value={false}>
                      <ClockCircleOutlined style={{ color: "#faad14" }} /> در انتظار
                    </Option>
                  </Select>
                </Col>
                <Col span={8}>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => onGetAllProjectPackages(gasProjectId)}
                    loading={loading}
                    block
                    size="large"
                    style={{ borderRadius: 8 }}
                  />
                </Col>
              </Row>
            </Space>
          ) : (
            // Desktop Filters Layout
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="جستجو (شماره پرونده، نام مالک، کد پستی...)"
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  allowClear
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Col>
              <Col xs={24} sm={12} md={10}>
                <Space size={8}>
                  <FilterOutlined style={{ color: "#8c8c8c" }} />
                  <Text>فیلتر وضعیت:</Text>
                  <Button
                    type={submitFilter === null ? "primary" : "default"}
                    onClick={() => setSubmitFilter(null)}
                    size="middle"
                  >
                    همه
                  </Button>
                  <Button
                    type={submitFilter === true ? "primary" : "default"}
                    onClick={() => setSubmitFilter(true)}
                    icon={<CheckCircleOutlined />}
                    style={{
                      background: submitFilter === true ? "#52c41a" : undefined,
                      borderColor: submitFilter === true ? "#52c41a" : undefined,
                    }}
                  >
                    ارسال شده
                  </Button>
                  <Button
                    type={submitFilter === false ? "primary" : "default"}
                    onClick={() => setSubmitFilter(false)}
                    icon={<ClockCircleOutlined />}
                    style={{
                      background: submitFilter === false ? "#faad14" : undefined,
                      borderColor: submitFilter === false ? "#faad14" : undefined,
                    }}
                  >
                    در انتظار
                  </Button>
                </Space>
              </Col>
              <Col xs={24} sm={24} md={6} style={{ textAlign: "left" }}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => onGetAllProjectPackages(gasProjectId)}
                  loading={loading}
                >
                  بروزرسانی
                </Button>
              </Col>
            </Row>
          )}
        </Card>

        {/* Content */}
        <Spin spinning={loading}>
          {groupedPackages.length === 0 ? (
            <Card style={{ borderRadius: 12, textAlign: "center", padding: isMobile ? 20 : 40 }}>
              <Empty
                description={
                  <Text type="secondary">پکیجی برای نمایش وجود ندارد</Text>
                }
              />
            </Card>
          ) : (
            <Collapse
              accordion
              defaultActiveKey={groupedPackages[0]?.fileNumber?.toString()}
              style={{ borderRadius: 12 }}
            >
              {groupedPackages.map(group => (
                <Panel
                  key={group.fileNumber}
                  header={
                    isMobile ? (
                      // Mobile Panel Header
                      <Space direction="vertical" size={4} style={{ width: "100%" }}>
                        <Space style={{ justifyContent: "space-between", width: "100%" }}>
                          <Space>
                            <FileTextOutlined style={{ color: "#1890ff" }} />
                            <Tag color="blue" style={{ fontFamily: "tahoma", margin: 0 }}>
                              {group.fileNumber}
                            </Tag>
                          </Space>
                          <Badge
                            count={group.packages.length}
                            style={{ backgroundColor: "#1890ff" }}
                          />
                        </Space>
                        <Text ellipsis style={{ fontSize: 12 }}>
                          <UserOutlined style={{ color: "#52c41a", marginLeft: 4 }} />
                          {group.landlordName}
                        </Text>
                      </Space>
                    ) : (
                      // Desktop Panel Header
                      <Row gutter={[16, 8]} align="middle" style={{ width: "100%" }}>
                        <Col xs={24} sm={6}>
                          <Space>
                            <FileTextOutlined style={{ color: "#1890ff" }} />
                            <Text strong>پرونده:</Text>
                            <Tag color="blue" style={{ fontFamily: "tahoma" }}>
                              {group.fileNumber}
                            </Tag>
                          </Space>
                        </Col>
                        <Col xs={24} sm={6}>
                          <Space>
                            <UserOutlined style={{ color: "#52c41a" }} />
                            <Text>{group.landlordName}</Text>
                          </Space>
                        </Col>
                        <Col xs={24} sm={6}>
                          <Space>
                            <EnvironmentOutlined style={{ color: "#722ed1" }} />
                            <Text type="secondary" style={{ fontFamily: "tahoma" }}>
                              {group.postalCode}
                            </Text>
                          </Space>
                        </Col>
                        <Col xs={24} sm={6}>
                          <Badge
                            count={group.packages.length}
                            style={{ backgroundColor: "#1890ff" }}
                          >
                            <Tag color="default">
                              <ToolOutlined /> پکیج
                            </Tag>
                          </Badge>
                        </Col>
                      </Row>
                    )
                  }
                >
                  {/* Project Info */}
                  <Card
                    size="small"
                    style={{
                      marginBottom: isMobile ? 12 : 16,
                      borderRadius: 8,
                      background: "#fafafa",
                    }}
                    bodyStyle={{ padding: isMobile ? 8 : 12 }}
                  >
                    {isMobile ? (
                      <Space direction="vertical" size={4} style={{ width: "100%" }}>
                        <Space size={4}>
                          <EnvironmentOutlined style={{ color: "#8c8c8c" }} />
                          <Text type="secondary" style={{ fontSize: 11 }}>آدرس:</Text>
                        </Space>
                        <Text style={{ fontSize: 12 }}>{group.address}</Text>
                        <Divider style={{ margin: "8px 0" }} />
                        <Space size={4}>
                          <UserOutlined style={{ color: "#8c8c8c" }} />
                          <Text type="secondary" style={{ fontSize: 11 }}>مهندس:</Text>
                          <Text style={{ fontSize: 12 }}>{group.engineerName}</Text>
                        </Space>
                        <Divider style={{ margin: "8px 0" }} />
                        <Space size={4}>
                          <UserOutlined style={{ color: "#8c8c8c" }} />
                          <Text type="secondary" style={{ fontSize: 11 }}>مجری:</Text>
                          <Text style={{ fontSize: 12 }}>{group.executorName}</Text>
                        </Space>
                      </Space>
                    ) : (
                      <Row gutter={[16, 8]}>
                        <Col xs={24} sm={12}>
                          <Space>
                            <EnvironmentOutlined />
                            <Text strong>آدرس:</Text>
                            <Text type="secondary">{group.address}</Text>
                          </Space>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Space>
                            <UserOutlined />
                            <Text strong>مهندس:</Text>
                            <Text type="secondary">{group.engineerName}</Text>
                          </Space>
                        </Col>
                        <Col xs={24} sm={12} style={{ marginTop: 8 }}>
                          <Space>
                            <UserOutlined />
                            <Text strong>مجری:</Text>
                            <Text type="secondary">{group.executorName}</Text>
                          </Space>
                        </Col>
                        <Col xs={24} sm={12} style={{ marginTop: 8 }}>
                            <Space>
                                <PhoneOutlined />
                                <Text strong>تلفن:</Text>
                                <Text type="secondary">{group.executorPhoneNumber}</Text>
                            </Space>
                        </Col>
                      </Row>
                    )}
                  </Card>

                  {/* Packages - Mobile or Desktop */}
                  {isMobile ? (
                    // Mobile Card View
                    <div>
                      {group.packages.map(pkg => renderPackageCard(pkg))}
                    </div>
                  ) : (
                    // Desktop Table View
                    <Table
                      columns={packageColumns}
                      dataSource={group.packages}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      bordered
                    />
                  )}
                </Panel>
              ))}
            </Collapse>
          )}
        </Spin>

        {/* Edit Modal */}
        <Modal
          open={!!editingPackage}
          onCancel={() => {
            setEditingPackage(null)
            form.resetFields()
          }}
          footer={null}
          title={
            <Space>
              <EditOutlined style={{ color: "#1890ff" }} />
              <span>ثبت سریال </span>
            </Space>
          }
          width={isMobile ? "95%" : 500}
          centered
          destroyOnClose
          style={{ maxWidth: 500 }}
        >
          {editingPackage && (
            <>
              {/* Package Info */}
              <Card
                size="small"
                style={{
                  marginBottom: 16,
                  borderRadius: 8,
                  background: "#f5f5f5",
                }}
                bodyStyle={{ padding: isMobile ? 8 : 12 }}
              >
                <Row gutter={[8, 8]}>
                  <Col xs={12}>
                    <Space direction="vertical" size={2}>
                      <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12 }}>
                        شماره پرونده:
                      </Text>
                      <Tag color="blue" style={{ fontFamily: "tahoma" }}>
                        {editingPackage.fileNumber}
                      </Tag>
                    </Space>
                  </Col>
                  <Col xs={12}>
                    <Space direction="vertical" size={2}>
                      <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12 }}>
                        نام مالک:
                      </Text>
                      <Text strong style={{ fontSize: isMobile ? 12 : 14 }}>
                        {editingPackage.landlordName}
                      </Text>
                    </Space>
                  </Col>
                  <Col span={24}>
                    <Divider style={{ margin: "8px 0" }} />
                  </Col>
                  <Col xs={12}>
                    <Space direction="vertical" size={2}>
                      <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12 }}>
                        نصاب:
                      </Text>
                      <Text style={{ fontSize: isMobile ? 12 : 14 }}>
                        {editingPackage.packageInstallerName}
                      </Text>
                    </Space>
                  </Col>
                  <Col xs={12}>
                    <Space direction="vertical" size={2}>
                      <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12 }}>
                        شرکت:
                      </Text>
                      <Text style={{ fontSize: isMobile ? 12 : 14 }}>
                        {editingPackage.packageInstallerCompanyName || "—"}
                      </Text>
                    </Space>
                  </Col>
                </Row>
              </Card>

              {/* Form */}
              <Form form={form} layout="vertical">
                <Form.Item
                  name="serialNumber"
                  label={
                    <Space>
                      <NumberOutlined />
                      <span>شماره سریال </span>
                    </Space>
                  }
                  rules={[
                    { required: true, message: "لطفاً شماره سریال را وارد کنید" },
                    { min: 3, message: "شماره سریال باید حداقل 3 کاراکتر باشد" },
                  ]}
                >
                  <Input
                    placeholder="شماره سریال  را وارد کنید"
                    size="large"
                    style={{ borderRadius: 8, fontFamily: "tahoma" }}
                    prefix={<NumberOutlined style={{ color: "#bfbfbf" }} />}
                  />
                </Form.Item>

                <div style={{ marginTop: -8, marginBottom: 16 }}>
                  <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      سریال مجاز پس از تبدیل:
                    </Text>
                    <Tag color="blue" style={{ fontFamily: "tahoma", marginRight: 0 }}>
                      {normalizedSerialPreview || "—"}
                    </Tag>
                    {serialDiffersFromInput && (
                      <Alert
                        type="warning"
                        showIcon
                        message="کاراکترهای غیرمجاز حذف یا تبدیل می‌شوند. فقط حروف انگلیسی و اعداد مجازند."
                        style={{ marginTop: 4 }}
                      />
                    )}
                  </Space>
                </div>

                <Form.Item
                  name="description"
                    rules={[
                    { required: true, message: "محل نصب پکیج/آبگرمکن را وارد کنید" },
                  ]}
                  label={
                    <Space>
                      <FileTextOutlined />
                      <span>توضیحات (محل نصب پکیج/آبگرمکن)</span>
                    </Space>
                  }
                >
                  <TextArea
                    rows={3}
                    placeholder="محل نصب پکیج/آبگرمکن را وارد کنید" 
                    style={{ borderRadius: 8 }}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, marginTop: isMobile ? 16 : 24 }}>
                  {isMobile ? (
                    <Space direction="vertical" size={8} style={{ width: "100%" }}>
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={handleSubmitSerial}
                        loading={updateSerialLoading}
                        size="large"
                        block
                        style={{ borderRadius: 8 }}
                      >
                        {updateSerialLoading ? "در حال ذخیره..." : "ثبت سریال"}
                      </Button>
                      <Button
                        icon={<CloseOutlined />}
                        onClick={() => {
                          setEditingPackage(null)
                          form.resetFields()
                        }}
                        size="large"
                        block
                        style={{ borderRadius: 8 }}
                      >
                        انصراف
                      </Button>
                    </Space>
                  ) : (
                    <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                      <Button
                        icon={<CloseOutlined />}
                        onClick={() => {
                          setEditingPackage(null)
                          form.resetFields()
                        }}
                        size="large"
                        style={{ borderRadius: 8 }}
                      >
                        انصراف
                      </Button>
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={handleSubmitSerial}
                        loading={updateSerialLoading}
                        size="large"
                        style={{ borderRadius: 8, minWidth: 120 }}
                      >
                        {updateSerialLoading ? "در حال ذخیره..." : "ثبت سریال"}
                      </Button>
                    </Space>
                  )}
                </Form.Item>
              </Form>
            </>
          )}
        </Modal>
    </>
  )

  // Return based on mode
  if (embedded) {
    return <div style={{ padding: 16 }}>{renderContent()}</div>
  }

  return (
    <>
      <MetaTags>
        <title>پکیج‌ها | واحد گاز</title>
      </MetaTags>
      <div className="page-content">{renderContent()}</div>
    </>
  )
}

const mapStateToProps = ({ ProjectPackages }) => ({
  allProjectPackages: ProjectPackages.allProjectPackages,
  loading: ProjectPackages.loading,
  updateSerialLoading: ProjectPackages.updateSerialLoading,
  updateSerialSuccess: ProjectPackages.updateSerialSuccess,
  updateSerialError: ProjectPackages.updateSerialError,
})

const mapDispatchToProps = dispatch => ({
  onGetAllProjectPackages: (gasProjectId) => dispatch(getAllProjectPackages(gasProjectId)),
  onUpdateProjectPackageSerial: data => dispatch(updateProjectPackageSerial(data)),
  onResetFlag: () => dispatch(resetProjectPackagesFlag()),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(ProjectPackageList))
